import { Logger } from './../@core/logger.service';

import { HttpClient } from '@angular/common/http';
import { CredentialsService } from '@shared/services/web/credentials.service';
import { AuthServerProvider } from './auth-jwt.service';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SERVER_API_URL } from '@app/app.constants';
import { LoginContext } from '@app/@shared/model/logincontext.model';
// import { SocialLoginService } from 'ngx-social-login-pro';

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private credentialsService: CredentialsService,
    private authJwtService: AuthServerProvider,
    private http: HttpClient // private socialAuthService: SocialLoginService
  ) {
    const log = new Logger('Auth');
  }

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext): Observable<any> {
    let data: any = null;

    const authenticateSuccess = (resp: any) => {
      data = {
        id_person: resp.person.id_person,
        id_user: resp.user.id_user,
        email: resp.user.email,
        first_name: resp.person.first_name,
        last_name: resp.person.last_name,
        phone: this.getPhone(resp.person.phone),
        authorities: resp.user.authorities,
        great_investor_status: resp.user.great_investor_status,
        has_dni: resp.user.has_dni,
        verified: resp.user.verified,
        profile_type: resp?.user?.profile_type,
        userAnswers: resp?.user?.userAnswers,
        userTypes: resp?.user?.userTypes
      };

      return this.credentialsService.setCredentials(data, context.remember);
    };

    return this.authJwtService
      .login(context)
      .pipe(map(authenticateSuccess.bind(this)));
  }

  loginWithHash(hash: string): Observable<any> {
    let data: any = null;
    return this.authJwtService.loginWithHash(hash).pipe(
      map((resp) => {
        data = {
          id_person: resp.person.id_person,
          id_user: resp.user.id_user,
          email: resp.user.email,
          first_name: resp.person.first_name,
          last_name: resp.person.last_name,
          phone: this.getPhone(resp.person.phone),
          authorities: resp.user.authorities,
          verified: resp.user.verified,
          has_dni: resp.user.has_dni,
        };
        return this.credentialsService.setCredentials(data);
      })
    );
  }

  loginHashOnly(hash: any): Observable<any> {
    return this.http.post(
      SERVER_API_URL + 'auth/hash-sign-in',
      { validationHash: hash },
      { observe: 'response' }
    );
  }

  loginFb(user: any): Observable<any> {
    const token = user.authToken;
    const email = user.email;
    const general_data = user?.general_data;
    let data: any = null;

    const authenticateSuccess = (resp: any) => {
      data = {
        id_person: resp?.person?.id_person,
        id_user: resp?.user?.id_user,
        email: resp?.user?.email,
        first_name: resp?.person?.first_name,
        last_name: resp?.person?.last_name,
        phone: this.getPhone(resp?.person?.phone),
        authorities: resp?.user?.authorities,
        social_user: resp?.social_user,
        has_dni: resp?.user?.has_dni,
        verified: resp?.user?.verified,
      };

      return this.credentialsService.setCredentials(data, false);
    };

    return this.authJwtService
      .loginFb({ token, email, general_data })
      .pipe(map(authenticateSuccess.bind(this)));
  }

  loginGoogle(token: any): Observable<any> {
    let data: any = null;

    const authenticateSuccess = (resp: any) => {
      data = {
        id_person: resp.person.id_person,
        id_user: resp.user.id_user,
        email: resp.user.email,
        first_name: resp.person.first_name,
        last_name: resp.person.last_name,
        phone: this.getPhone(resp.person.phone),
        authorities: resp.user.authorities,
        social_user: resp.social_user,
        has_dni: resp.user.has_dni,
        verified: resp.user.verified,
      };
      return this.credentialsService.setCredentials(data, false);
    };

    return this.authJwtService
      .loginGoogle(token)
      .pipe(map(authenticateSuccess.bind(this)));
  }
  getPhone(phone: any): string {
    return phone?.code_country
      ? phone?.code_country + '-' + phone?.number
      : phone?.number;
  }

  verifyAccount(hash: string): Observable<any> {
    return this.http.post(
      SERVER_API_URL + 'auth/verify-account/' + hash,
      null,
      { observe: 'response' }
    );
  }

  checkHashRecoverPassword(hash: string): Observable<any> {
    return this.http.post(
      SERVER_API_URL + 'auth/check-hash-recover-password/' + hash,
      null,
      {
        observe: 'response',
      }
    );
  }

  recoverPassword(form: any): Observable<any> {
    const data = {
      email: form.email,
    };
    return this.http.post(SERVER_API_URL + 'auth/recover-password', data, {
      observe: 'response',
    });
  }

  recoverPasswordFinish(
    form: any,
    email: string,
    hash: string
  ): Observable<any> {
    const data = {
      email,
      password: form.password,
      hash,
    };
    return this.http.post(
      SERVER_API_URL + 'auth/recover-password-finish/',
      data,
      {
        observe: 'response',
      }
    );
  }

  unsubscribeConsents(data: any): Observable<any> {
    return this.http.post(SERVER_API_URL + 'auth/unsubscribe-consents', data);
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    // Customize credentials invalidation here
    this.credentialsService.setCredentials().subscribe();
    this.authJwtService.logout().subscribe();
    // this.socialAuthService.logout().subscribe({
    //   complete: () => console.log('Logout success'),
    //   error: (err) => console.log(err),
    // });
    return of(true);
  }
}
